import { apiClient } from "@/integrations/api/client/client";
import {
  AddMembershipResponse,
  AdminMemberApi,
  InvitationApi,
  MembershipApi,
} from "@/integrations/api/admin/tenant";

function unwrap<T>(value: { data?: T; error?: { message?: string } }): T {
  if (value.error) throw new Error(value.error.message || "Request failed");
  return value.data as T;
}

export async function fetchMyMemberships(): Promise<MembershipApi[]> {
  // apiClient.request falls back to `{} as T` when the envelope has no data
  // field (client.ts), so coerce non-array responses to [] to keep callers
  // (TenantContext.mergeTenantInfo) safely iterable.
  const result = unwrap(await apiClient.request<MembershipApi[]>("/v1/me/memberships"));
  return Array.isArray(result) ? result : [];
}

// TODO(add-another-hospital-page): callers should also POST /v1/auth/refresh
// after this resolves so the access token's `memberships` claim picks up the
// new entry without forcing logout. No "Add another hospital" page exists in
// the dashboard yet — when product asks for one, see DASHBOARD_INTEGRATION.md
// §"Add another hospital (deferred)" for the recommended location.
export async function joinTenant(
  tenantSlug: string
): Promise<AddMembershipResponse> {
  return unwrap(
    await apiClient.request<AddMembershipResponse>("/v1/me/memberships", {
      method: "POST",
      body: JSON.stringify({ tenantSlug }),
    })
  );
}

// Tenant-scoped admin endpoints (require X-Tenant-Id — apiClient injects it).
export async function fetchTenantMembers(): Promise<AdminMemberApi[]> {
  // Backend wraps the array as `{ items: [...] }` inside the envelope's data —
  // see TenantMembersController.ListMembers — so unwrap and pull items out.
  const result = unwrap(
    await apiClient.request<{ items?: AdminMemberApi[] }>("/v1/admin/members")
  );
  return Array.isArray(result?.items) ? result.items : [];
}

export async function removeTenantMember(userId: string): Promise<void> {
  await unwrap(
    await apiClient.request<void>(`/v1/admin/members/${encodeURIComponent(userId)}`, {
      method: "DELETE",
    })
  );
}

// Backend `InvitationDto` returns createdAt/expiresAt/acceptedAt/revokedAt but
// no `status` or `invitedAt` — derive them so the UI shows a meaningful badge.
interface RawInvitation {
  id: number;
  email: string;
  roleName: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string | null;
  revokedAt?: string | null;
  token?: string;
}

function deriveInvitationStatus(raw: RawInvitation): InvitationApi["status"] {
  if (raw.revokedAt) return "revoked";
  if (raw.acceptedAt) return "accepted";
  if (raw.expiresAt && new Date(raw.expiresAt).getTime() < Date.now()) return "expired";
  return "pending";
}

function normalizeInvitation(raw: RawInvitation): InvitationApi {
  return {
    id: raw.id,
    email: raw.email,
    roleName: raw.roleName,
    token: raw.token,
    invitedAt: raw.createdAt,
    expiresAt: raw.expiresAt,
    acceptedAt: raw.acceptedAt ?? null,
    status: deriveInvitationStatus(raw),
  };
}

export async function fetchInvitations(): Promise<InvitationApi[]> {
  // Backend wraps as `{ items: [...] }` (see TenantMembersController.ListInvitations).
  const result = unwrap(
    await apiClient.request<{ items?: RawInvitation[] }>("/v1/admin/members/invitations")
  );
  return Array.isArray(result?.items) ? result.items.map(normalizeInvitation) : [];
}

export async function createInvitation(input: {
  email: string;
  roleName: string;
}): Promise<InvitationApi> {
  const raw = unwrap(
    await apiClient.request<RawInvitation>("/v1/admin/members/invitations", {
      method: "POST",
      body: JSON.stringify(input),
    })
  );
  return normalizeInvitation(raw);
}

export async function revokeInvitation(id: number): Promise<void> {
  await unwrap(
    await apiClient.request<void>(`/v1/admin/members/invitations/${id}`, {
      method: "DELETE",
    })
  );
}

// Public endpoint (no auth) — invitee lands on /accept-invitation?token=…
// Backend: POST /v1/invitations/accept (TenantsController.AcceptInvitation).
// Returns 200 with a plain success message on success.
export async function acceptInvitation(input: {
  token: string;
  password: string;
  fullName?: string;
}): Promise<void> {
  await unwrap(
    await apiClient.request<void>("/v1/invitations/accept", {
      method: "POST",
      body: JSON.stringify({
        token: input.token,
        password: input.password,
        fullName: input.fullName ?? null,
      }),
    })
  );
}
